// Choosing things, in our own controls rather than the browser's (D-062): Select (WAI-ARIA select-only
// combobox), Combobox (type and pick), Listbox, Checkbox, Switch and RadioGroup. All work with the
// keyboard alone and read correctly with a screen reader.
import { Children, isValidElement, useEffect, useId, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { FieldShell } from './controls';
import { fieldDescription, needsShell } from './field-description';
import { Popover } from './layer';
import { useFocusOnMount } from './focus';
import { optionIndex } from './option-index';
import { useT } from '../i18n';

export interface SelectOption<T extends string | number = string> { value: T; label: string; hint?: string; icon?: ReactNode; disabled?: boolean }

/** `<option>` children (the old native API) as options. */
function optionsFrom(children: ReactNode): SelectOption<string>[] {
  const out: SelectOption<string>[] = [];
  const walk = (n: ReactNode) => Children.forEach(n, (c) => {
    if (!isValidElement(c)) return;
    const p = c.props as { value?: string | number; children?: ReactNode; disabled?: boolean; label?: string };
    if (c.type === 'option') out.push({ value: String(p.value ?? (typeof p.children === 'string' ? p.children : '')), label: p.label ?? Children.toArray(p.children).join(''), disabled: p.disabled });
    else if (p.children) walk(p.children);
  });
  walk(children);
  return out;
}

const typeahead = () => ({ s: '', t: 0 });
function nextByLetter<T extends { label: string; disabled?: boolean }>(list: T[], buf: { s: string; t: number }, key: string, from: number): number {
  const now = Date.now();
  buf.s = (now - buf.t < 600 ? buf.s : '') + key.toLowerCase(); buf.t = now;
  const start = buf.s.length === 1 ? from + 1 : from;
  for (let k = 0; k < list.length; k++) {
    const i = (start + k) % list.length;
    if (!list[i].disabled && list[i].label.toLowerCase().startsWith(buf.s)) return i;
  }
  return -1;
}
const step = (list: { disabled?: boolean }[], from: number, by: number) => {
  for (let i = from + by; i >= 0 && i < list.length; i += by) if (!list[i].disabled) return i;
  return from;
};

export interface SelectProps<T extends string | number = string> {
  label: ReactNode;
  /** No visible label: the control sits inline (its name is still `label`). */
  labelHidden?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  value?: T;
  options?: SelectOption<T>[];
  /** `<option>` elements, as with a native select. */
  children?: ReactNode;
  onValueChange?(value: T): void;
  /** The native shape (`e.target.value`), so existing code keeps working. */
  onChange?(e: ChangeEvent<HTMLSelectElement>): void;
  placeholder?: string;
  /** A search box at the top of the list (long lists: time zones, countries). */
  searchable?: boolean;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  className?: string;
  icon?: ReactNode;
  size?: 'sm' | 'md';
  testId?: string;
  'data-testid'?: string;
}

/** A button showing the choice, opening a list (WAI-ARIA select-only combobox). */
export function Select<T extends string | number = string>(props: SelectProps<T>) {
  const t = useT();
  const { label, labelHidden, hint, error, value, onValueChange, onChange, placeholder, searchable, disabled, required, name, id, className = '', icon, size = 'md', testId } = props;
  const auto = useId(); const fid = id ?? auto;
  const options = useMemo(() => (props.options ?? (optionsFrom(props.children) as unknown as SelectOption<T>[])), [props.options, props.children]);
  const btn = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [q, setQ] = useState('');
  const buf = useRef(typeahead());
  const current = options.find((o) => String(o.value) === String(value));
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    // Words that start with what was typed come first ("am" → Amsterdam before Bamako).
    const rank = (o: SelectOption<T>) => { const l = o.label.toLowerCase(); return l.startsWith(s) ? 0 : l.split(/[\s/,()-]+/).some((w) => w.startsWith(s)) ? 1 : l.includes(s) || (o.hint ?? '').toLowerCase().includes(s) ? 2 : 3; };
    return options.map((o) => [rank(o), o] as const).filter(([r]) => r < 3).sort((a, b) => a[0] - b[0]).map(([, o]) => o);
  }, [options, q]);
  const show = () => { if (disabled) return; setQ(''); setActive(Math.max(0, options.findIndex((o) => String(o.value) === String(value)))); setOpen(true); };
  const pick = (o: SelectOption<T> | undefined) => {
    if (!o || o.disabled) return;
    setOpen(false);
    if (String(o.value) !== String(value)) {
      onValueChange?.(o.value);
      if (onChange) { const target = { value: String(o.value), name }; onChange({ target, currentTarget: target } as unknown as ChangeEvent<HTMLSelectElement>); }
    }
    btn.current?.focus();
  };
  useEffect(() => { if (open) document.getElementById(`${fid}-o${active}`)?.scrollIntoView({ block: 'nearest' }); }, [open, active, fid]);
  const onKey = (e: KeyboardEvent) => {
    const k = e.key;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(k) || (k === 'ArrowDown' && e.altKey)) { e.preventDefault(); show(); }
      else if (k.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Typing on a closed select changes the choice, as a native one does.
        const i = nextByLetter(options, buf.current, k, options.findIndex((o) => String(o.value) === String(value)));
        if (i >= 0) { e.preventDefault(); pick(options[i]); }
      }
      return;
    }
    if (k === 'ArrowDown') { e.preventDefault(); setActive((a) => step(shown, a, 1)); }
    else if (k === 'ArrowUp') { e.preventDefault(); setActive((a) => step(shown, a, -1)); }
    else if (k === 'Home' && !searchable) { e.preventDefault(); setActive(step(shown, -1, 1)); }
    else if (k === 'End' && !searchable) { e.preventDefault(); setActive(step(shown, shown.length, -1)); }
    else if (k === 'PageDown') { e.preventDefault(); setActive((a) => Math.min(shown.length - 1, a + 8)); }
    else if (k === 'PageUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 8)); }
    else if (k === 'Enter' || (k === ' ' && !searchable)) { e.preventDefault(); pick(shown[active]); }
    else if (k === 'Tab') { pick(shown[active]); }
    else if (!searchable && k.length === 1 && !e.ctrlKey && !e.metaKey) { e.preventDefault(); const i = nextByLetter(shown, buf.current, k, active); if (i >= 0) setActive(i); }
  };
  const control = (
    <>
      <button ref={btn} type="button" id={fid} role="combobox" aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? `${fid}-list` : undefined}
        aria-labelledby={labelHidden ? undefined : `${fid}-label ${fid}-value`} aria-label={labelHidden && typeof label === 'string' ? `${label}: ${current?.label ?? placeholder ?? ''}` : undefined}
        aria-activedescendant={open && !searchable && shown[active] ? `${fid}-o${active}` : undefined} aria-invalid={error ? true : undefined} aria-required={required || undefined}
        aria-describedby={fieldDescription(fid, hint, error)?.id}
        className={`dz-selectbtn${size === 'sm' ? ' dz-selectbtn--sm' : ''}${labelHidden ? ' dz-selectbtn--inline' : ''} ${className}`} disabled={disabled}
        onClick={() => (open ? setOpen(false) : show())} onKeyDown={onKey} data-testid={testId ?? props['data-testid']}>
        {icon ?? current?.icon}
        <span className={`dz-selectbtn__text${current ? '' : ' is-placeholder'}`} id={`${fid}-value`}>{current?.label ?? placeholder ?? ''}</span>
        <ChevronDown aria-hidden className="dz-selectbtn__chev" />
      </button>
      {name && <input type="hidden" name={name} value={value === undefined ? '' : String(value)} />}
      <Popover open={open} onClose={() => setOpen(false)} anchor={btn.current} gap={8} matchWidth role="presentation" className="dz-listlayer" focusOnOpen={!!searchable} returnFocus sheet={options.length > 12}>
        {searchable && (
          <div className="dz-listsearch">
            <Search aria-hidden />
            <input data-autofocus role="combobox" aria-expanded aria-controls={`${fid}-list`} aria-autocomplete="list" aria-label={t('ui.select.search_in', { label: typeof label === 'string' ? label.toLowerCase() : '' })}
              aria-activedescendant={shown[active] ? `${fid}-o${active}` : undefined} value={q} placeholder={t('ui.select.search')}
              onChange={(e) => { setQ(e.target.value); setActive(0); }} onKeyDown={onKey} autoComplete="off" spellCheck={false} />
          </div>
        )}
        {/* Focus stays on the button or the search field (aria-activedescendant); the list takes the same keys if it ever has focus. */}
        <div role="listbox" id={`${fid}-list`} aria-label={typeof label === 'string' ? label : undefined} className="dz-listbox" tabIndex={-1}
          onKeyDown={onKey} onClick={(e) => { const i = optionIndex(e); if (i >= 0) pick(shown[i]); }}>
          {shown.map((o, i) => (
            <div key={String(o.value)} id={`${fid}-o${i}`} role="option" data-index={i} aria-selected={String(o.value) === String(value)} aria-disabled={o.disabled || undefined}
              className={`dz-option${i === active ? ' is-active' : ''}`}
              onPointerDown={(e) => e.preventDefault()} onPointerMove={() => { if (i !== active) setActive(i); }}>
              {o.icon}<span className="dz-option__label">{o.label}{o.hint && <small>{o.hint}</small>}</span>
              {String(o.value) === String(value) && <Check aria-hidden className="dz-option__check" />}
            </div>
          ))}
          {shown.length === 0 && <div className="dz-option dz-option--empty" role="presentation">{t('ui.select.none')}</div>}
        </div>
      </Popover>
    </>
  );
  // A hidden label drops only the label: a hint or an error still shows, where aria-describedby points (DZ-10801).
  if (!needsShell(labelHidden, hint, error)) return control;
  return <FieldShell label={label} hint={hint} error={error} id={fid} labelAs="span" labelHidden={labelHidden}>{control}</FieldShell>;
}

/** A list always on screen (WAI-ARIA listbox): arrows move and choose, letters jump. */
export function Listbox<T extends string | number = string>({ label, value, options, onChange, testId, className = '', focusOnOpen }: {
  label: string; value: T | null; options: SelectOption<T>[]; onChange(v: T): void; testId?: string; className?: string; focusOnOpen?: boolean;
}) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const buf = useRef(typeahead());
  const sel = options.findIndex((o) => o.value === value);
  useEffect(() => {
    const el = sel >= 0 ? document.getElementById(`${id}-${sel}`) : null;
    const list = ref.current;
    if (el && list) list.scrollTop = el.offsetTop - list.clientHeight / 2 + el.offsetHeight / 2;
  }, [sel, id]);
  useEffect(() => { if (focusOnOpen) ref.current?.focus({ preventScroll: true }); }, [focusOnOpen]);
  const go = (i: number) => { const o = options[i]; if (o && !o.disabled) onChange(o.value); };
  return (
    <div ref={ref} role="listbox" tabIndex={0} aria-label={label} aria-activedescendant={sel >= 0 ? `${id}-${sel}` : undefined} className={`dz-listbox dz-listbox--inline ${className}`} data-testid={testId}
      onClick={(e) => { const i = optionIndex(e); if (i >= 0) go(i); }}
      onKeyDown={(e) => {
        const k = e.key;
        if (k === 'ArrowDown') { e.preventDefault(); go(step(options, sel, 1)); }
        else if (k === 'ArrowUp') { e.preventDefault(); go(step(options, sel < 0 ? options.length : sel, -1)); }
        else if (k === 'Home') { e.preventDefault(); go(step(options, -1, 1)); }
        else if (k === 'End') { e.preventDefault(); go(step(options, options.length, -1)); }
        else if (k.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && k !== ' ') { const i = nextByLetter(options, buf.current, k, sel); if (i >= 0) { e.preventDefault(); go(i); } }
      }}>
      {options.map((o, i) => (
        <div key={String(o.value)} id={`${id}-${i}`} role="option" data-index={i} aria-selected={i === sel} aria-disabled={o.disabled || undefined} className={`dz-option${i === sel ? ' is-active' : ''}`}
          onPointerDown={(e) => { e.preventDefault(); ref.current?.focus({ preventScroll: true }); }}>
          <span className="dz-option__label">{o.label}{o.hint && <small>{o.hint}</small>}</span>
        </div>
      ))}
    </div>
  );
}

export interface ComboboxProps<T> {
  value: string; onInput(v: string): void; items: T[]; onPick(item: T): void; render(item: T): ReactNode; label: string;
  placeholder?: string; testId?: string; className?: string; inputClass?: string;
  /** Return true when Enter was handled (the list closes). */
  onEnter?(): boolean | void; onBlur?(): void; onFocus?(): void; onKeyDown?(e: KeyboardEvent<HTMLInputElement>): void;
  focusOnOpen?: boolean; emptyText?: string; minWidth?: number;
  /** Enter takes the first suggestion even before the person moved through the list. */
  enterPicksFirst?: boolean;
}

/** Type, and pick from suggestions (WAI-ARIA combobox with a listbox). Free text is allowed. */
export function Combobox<T>({ value, onInput, items, onPick, render, label, placeholder, testId, className = '', inputClass = '', onEnter, onBlur, onFocus, onKeyDown, focusOnOpen, emptyText, minWidth, enterPicksFirst = true }: ComboboxProps<T>) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  useFocusOnMount(input, focusOnOpen);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const moved = useRef(false);
  useEffect(() => { setActive(items.length ? 0 : -1); moved.current = false; }, [items]);
  useEffect(() => { if (open && active >= 0) document.getElementById(`${id}-o${active}`)?.scrollIntoView({ block: 'nearest' }); }, [open, active, id]);
  const show = open && (items.length > 0 || !!emptyText);
  const pick = (i: number) => { const it = items[i]; if (it !== undefined) { onPick(it); setOpen(false); } };
  return (
    <div className={`dz-combobox ${className}`}>
      <input ref={input} className={`dz-combobox__input ${inputClass}`} role="combobox" aria-autocomplete="list" aria-expanded={show} aria-controls={`${id}-list`} aria-label={label}
        aria-activedescendant={show && active >= 0 ? `${id}-o${active}` : undefined} value={value} placeholder={placeholder} autoComplete="off" spellCheck={false} data-testid={testId}
        onChange={(e) => { onInput(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); onFocus?.(); }}
        onClick={() => setOpen(true)}
        onBlur={() => { setOpen(false); onBlur?.(); }}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (e.defaultPrevented) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); moved.current = true; if (!open) setOpen(true); else setActive((a) => Math.min(items.length - 1, a + 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); moved.current = true; setActive((a) => Math.max(0, a - 1)); }
          else if (e.key === 'Enter') {
            if (onEnter?.()) { e.preventDefault(); setOpen(false); return; }
            if (show && active >= 0 && (enterPicksFirst || moved.current)) { e.preventDefault(); pick(active); }
            else if (show) { e.preventDefault(); setOpen(false); }
          }
        }} />
      <Popover open={show} onClose={() => setOpen(false)} anchor={input.current} matchWidth role="presentation" className="dz-listlayer" focusOnOpen={false} returnFocus={false} closeOnFocusOut={false}
        style={minWidth ? { minWidth } : undefined}>
        {/* Focus stays in the field (aria-activedescendant); Enter on the list, if it ever has focus, picks as in the field. */}
        <div role="listbox" id={`${id}-list`} aria-label={label} className="dz-listbox" tabIndex={-1}
          onClick={(e) => pick(optionIndex(e))} onKeyDown={(e) => { if (e.key === 'Enter' && active >= 0) { e.preventDefault(); pick(active); } }}>
          {items.map((it, i) => (
            <div key={i} id={`${id}-o${i}`} role="option" data-index={i} aria-selected={i === active} className={`dz-option${i === active ? ' is-active' : ''}`}
              onPointerDown={(e) => e.preventDefault()} onPointerMove={() => { moved.current = true; setActive(i); }}>{render(it)}</div>
          ))}
          {items.length === 0 && emptyText && <div className="dz-option dz-option--empty" role="presentation">{emptyText}</div>}
        </div>
      </Popover>
    </div>
  );
}

// Checkbox, Switch and RadioGroup live in toggles.tsx (small, and on most first screens); re-exported here.
export { Checkbox, RadioGroup, Switch, type CheckboxProps, type RadioGroupProps, type SwitchProps } from './toggles';
